import { convertToParamMap, type ParamMap, type Router } from '@angular/router';

export interface MyTimesheetDeepLinkParams {
  view: 'weekly';
  weekStart: string;
  projectId?: number;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True when the URL contains a date param the parser should attempt. */
export function hasMyTimesheetDeepLinkDateAttempt(qp: ParamMap): boolean {
  return !!(qp.get('weekStart')?.trim() || qp.get('date')?.trim());
}

/** Returns Monday (YYYY-MM-DD) for any day in the same Mon–Sun week. */
export function normalizeToWeekStartMonday(isoDate: string): string | null {
  if (!isValidIsoDateString(isoDate)) return null;
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const dow = d.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  const mon = new Date(d);
  mon.setDate(d.getDate() + diff);
  return dateToIsoString(mon);
}

/** Parse `?weekStart=…` from a router URL or path+query string (email / returnUrl links). */
export function queryParamMapFromUrl(router: Router, url: string): ParamMap {
  const merged = new Map<string, string>();
  const fromRouter = router.parseUrl(url).queryParams;
  for (const [key, raw] of Object.entries(fromRouter)) {
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (value != null && value !== '') merged.set(key, value);
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.pathname.includes('/timesheet/my-timesheet') ||
      window.location.pathname.includes('/timesheet/approval-timsheet'))
  ) {
    const usp = new URLSearchParams(window.location.search);
    usp.forEach((value, key) => {
      if (value !== '') merged.set(key, value);
    });
  }
  const params: Record<string, string> = {};
  merged.forEach((value, key) => {
    params[key] = value;
  });
  return convertToParamMap(params);
}

export function parseMyTimesheetDeepLink(qp: ParamMap): MyTimesheetDeepLinkParams | null {
  const weekStartRaw = qp.get('weekStart')?.trim();
  const dateRaw = qp.get('date')?.trim();
  const anchor = weekStartRaw || dateRaw;
  if (!anchor) return null;

  const weekStart = normalizeToWeekStartMonday(anchor);
  if (!weekStart) return null;

  const view = qp.get('view')?.trim().toLowerCase();
  if (view && view !== 'weekly') return null;

  const projectIdRaw = qp.get('projectId')?.trim();
  let projectId: number | undefined;
  if (projectIdRaw) {
    const n = Number(projectIdRaw);
    if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) return null;
    projectId = n;
  }

  return { view: 'weekly', weekStart, ...(projectId != null ? { projectId } : {}) };
}

function isValidIsoDateString(s: string): boolean {
  if (!ISO_DATE_RE.test(s)) return false;
  const d = new Date(`${s}T12:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  return dateToIsoString(d) === s;
}

function dateToIsoString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
